import {
  Button,
  Carousel,
  CarouselNav,
  FadeTransition,
  getSlideProgress,
  NavDots,
  NavPrevButton,
  NavTextButton,
  useCarousel
} from "@9amhealth/shared";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef } from "react";
import { Dialog, Modal as RAModal, ModalOverlay } from "react-aria-components";
import UserCubit, {
  ProfileAttributesKeys
} from "src/state/UserCubit/UserCubit";
import Translate from "src/ui/components/Translate/Translate";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import HomePageExpectationManagement from "src/ui/assets/images/home-page-bg-hwj.webp";
import ExpectationSteps from "src/ui/components/ExpectationManagement/ExpectationSteps";
import ToolkitSlide from "src/ui/components/ExpectationManagement/ToolkitSlide";
import ExpectationsManagementGoals from "./ExpectationsManagementGoals";
import { ExpectationsManagementScreenBloc } from "src/ui/components/ExpectationManagement/ExpectationsManagementScreenBloc";
import { useBloc } from "src/state/state";
import { useBlocNext } from "@blac/react";
import { TranslationKey } from "src/types/translationKey";

const LAYOUT_SWITCH = APP_CONTENT_WIDTH_WITHOUT_PADDING + 200;

// Wrappers exist only to register this fullscreen flow in react-aria's
// overlay stack so it isn't inerted by any Modal opened underneath. The
// visual layout still lives on `ManagementScreenWrap` below.
const StackOverlay = styled(ModalOverlay)`
  display: contents;
`;

const StackPanel = styled(RAModal)`
  display: contents;
`;

const StackDialog = styled(Dialog)`
  display: contents;
  outline: none;
`;

const ManagementScreenWrap = styled(motion.div)`
  label: ManagementScreenWrap;
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  z-index: 2000;
`;

const VisuallyHidden = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const BackgroundImage = styled.img`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  object-fit: cover;
`;

const BackgroundImageGreen = styled(BackgroundImage)`
  filter: hue-rotate(260deg) saturate(0.6) brightness(1.12);
`;

const BackgroundStepToolkit = styled.div`
  z-index: 2;
  background: var(--color-cream);
  position: fixed;
  inset: 0;
`;

const OverlayNav = styled(CarouselNav)`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  z-index: 10;
  padding-top: calc(var(--safe-area-inset-top, 0px) + 1rem);

  @media (min-width: ${LAYOUT_SWITCH}px) {
    padding-top: calc(var(--safe-area-inset-top, 0px) + 1rem);
    padding-bottom: 1rem;
    border: 1px solid var(--Greys-Light-Gray, #e6e3db);
    background: rgba(255, 255, 255, 0.01);
    backdrop-filter: blur(14px);
  }
`;

const CarouselWrapper = styled.div`
  flex: 1;
  width: 100%;
  min-height: 0;
  position: relative;
  z-index: 5;
`;

const ContentWrapper = styled.div`
  height: 100%;
  width: 100%;
  --content-width: min(100%, ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px);
  --title-font-size: 2rem;
  padding: 0 1.5rem;
  padding-top: calc(var(--safe-area-inset-top, 0px) + 7rem);
  padding-bottom: calc(var(--safe-area-inset-bottom, 0px) + 7rem);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  @media (min-width: ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px) {
    --title-font-size: 2.5rem;
    padding-top: calc(var(--safe-area-inset-top, 0px) + 10rem);
    padding-bottom: calc(var(--safe-area-inset-bottom, 0px) + 10rem);
  }
`;

const BottomSection = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  padding: 1.5rem 1.5rem calc(var(--safe-area-inset-bottom, 0px) + 2em) 1.5rem;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.7) 100%
  );

  > button {
    max-width: ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px;
    width: 100%;
  }

  @media (min-width: ${LAYOUT_SWITCH}px) {
    padding: 1.5rem 1.5rem calc(var(--safe-area-inset-bottom, 0px) + 1.5em)
      1.5rem;
    border-top: 1px solid var(--Greys-Light-Gray, #e6e3db);
    background: rgba(255, 255, 255, 0.01);
    backdrop-filter: blur(14px);
  }
`;

const CAROUSEL_ID = "expectations-management-carousel";

const ExpectationsManagementScreenContent = (props: {
  onDismiss: () => void;
}) => {
  const [{ profileAttributes }] = useBloc(UserCubit);
  const [state, bloc] = useBlocNext(ExpectationsManagementScreenBloc);
  const carousel = useCarousel(CAROUSEL_ID);
  const stepTollkitBackground = useRef<HTMLDivElement>(null);
  const goalsBackground = useRef<HTMLImageElement>(null);

  const userExperienceGroup =
    profileAttributes?.[ProfileAttributesKeys.experienceGroup];
  const programMemberships =
    profileAttributes?.[ProfileAttributesKeys.programMemberships];
  const programMembership = programMemberships?.[0]?.program;

  bloc.configure({
    userExperienceGroup,
    callbacks: { onDismiss: props.onDismiss }
  });

  const handleCtaPress = () => {
    if (carousel.isLastSlide) {
      bloc.handleDismissScreen("get-started-button");
    } else {
      carousel.scrollNext();
    }
  };

  const ctaText: TranslationKey = carousel.isLastSlide ? "getStarted" : "next";

  const scrollSnapsRef = useRef(carousel.scrollSnaps);
  scrollSnapsRef.current = carousel.scrollSnaps;

  const lastToolkitOpacity = useRef<number>(-1);
  const lastGoalsOpacity = useRef<number>(-1);

  useEffect(() => {
    return carousel.subscribeToProgress((progress) => {
      const slide2Progress = getSlideProgress(
        1,
        progress,
        scrollSnapsRef.current
      );
      const slide3Progress = getSlideProgress(
        2,
        progress,
        scrollSnapsRef.current
      );

      const toolkitOpacity = Math.round(slide2Progress * 1000) / 1000;
      const goalsOpacity = Math.round(slide3Progress * 1000) / 1000;

      // Toolkit background fades in for slide 2 and stays visible for slide 3
      if (
        stepTollkitBackground.current &&
        toolkitOpacity !== lastToolkitOpacity.current
      ) {
        lastToolkitOpacity.current = toolkitOpacity;
        stepTollkitBackground.current.style.opacity = String(toolkitOpacity);
      }
      // Goals background fades in on top for slide 3
      if (
        goalsBackground.current &&
        goalsOpacity !== lastGoalsOpacity.current
      ) {
        lastGoalsOpacity.current = goalsOpacity;
        goalsBackground.current.style.opacity = String(goalsOpacity);
      }
    });
  }, [carousel.subscribeToProgress]);

  return (
    <ManagementScreenWrap
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, y: "100%" }}
      transition={{
        opacity: { duration: 0.25 },
        y: { type: "spring", damping: 30, stiffness: 300, mass: 0.8 }
      }}
    >
      <VisuallyHidden id="expectations-management-title">
        Welcome to your health journey
      </VisuallyHidden>
      <BackgroundImage
        src={HomePageExpectationManagement}
        alt=""
        aria-hidden="true"
        style={{ opacity: 1, zIndex: 1 }}
      />
      <BackgroundStepToolkit
        ref={stepTollkitBackground}
        aria-hidden="true"
        style={{ opacity: 0, zIndex: 2 }}
      />
      <BackgroundImageGreen
        ref={goalsBackground}
        src={HomePageExpectationManagement}
        alt=""
        aria-hidden="true"
        style={{ opacity: 0, zIndex: 3 }}
      />

      <OverlayNav
        id={CAROUSEL_ID}
        leftSlot={(api) => (
          <NavPrevButton onClick={api.scrollPrev} hidden={api.isFirstSlide} />
        )}
        centerSlot={(api) => (
          <NavDots
            selectedIndex={api.selectedIndex}
            totalSlides={api.totalSlides}
            scrollSnaps={api.scrollSnaps}
            onDotClick={api.scrollTo}
            subscribeToProgress={api.subscribeToProgress}
          />
        )}
        rightSlot={(api) => (
          <NavTextButton
            onClick={() => bloc.handleDismissScreen("skip-button")}
            disabled={api.isLastSlide}
          >
            <Translate msg="skip" />
          </NavTextButton>
        )}
      />

      <CarouselWrapper>
        <Carousel
          id={CAROUSEL_ID}
          onStepChange={bloc.handleStepChange}
          aria-label="Membership introduction"
        >
          <ContentWrapper data-carousel-slide-name="expectation-steps">
            <FadeTransition show={state.steps.length > 0}>
              <ExpectationSteps steps={state.steps} />
            </FadeTransition>
          </ContentWrapper>

          <ContentWrapper data-carousel-slide-name="toolkit-slide">
            <ToolkitSlide userExperienceGroup={userExperienceGroup} />
          </ContentWrapper>

          <ContentWrapper data-carousel-slide-name="goals-slide">
            <ExpectationsManagementGoals
              programMembership={programMembership}
            />
          </ContentWrapper>
        </Carousel>
      </CarouselWrapper>

      <BottomSection>
        <Button theme="charcoal" fullWidth onPress={handleCtaPress}>
          <Translate msg={ctaText} />
        </Button>
      </BottomSection>
    </ManagementScreenWrap>
  );
};

const ExpectationsManagementScreen = (props: {
  show: boolean;
  onDismiss: () => void;
}) => {
  // ModalOverlay handles its own portal — also rescues the carousel from
  // ion-page/ion-content, which is what the previous custom portal target
  // existed to do.
  return (
    <StackOverlay
      isOpen={props.show}
      isDismissable={false}
      isKeyboardDismissDisabled
    >
      <StackPanel>
        <StackDialog aria-labelledby="expectations-management-title">
          <AnimatePresence>
            {props.show && (
              <ExpectationsManagementScreenContent
                onDismiss={props.onDismiss}
              />
            )}
          </AnimatePresence>
        </StackDialog>
      </StackPanel>
    </StackOverlay>
  );
};

export default ExpectationsManagementScreen;
